import React, { useEffect, useState } from "react";
import { TextInput, Button, Card, Title, Text, Divider } from '@tremor/react';
import axios from "axios";
import { useAuth } from "../auth/AuthContext";

interface Loan {
    id: number;
    akareNo: number;
    carNos: string[];
    startDate: string;
    endDate?: string;
    createdAt?: string;
}

export default function AdminPage() {
    const [akareNo, setAkareNo] = useState("");
    const [carNo, setCarNo] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [loans, setLoans] = useState<Loan[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const { isAuthenticated, user } = useAuth();

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchLoans();
    }, [isAuthenticated]);

    const fetchLoans = async () => {
        setLoading(true);
        try {
            const res = await axios.get("/car-loans", { withCredentials: true });
            setLoans(res.data.loans || []);
        } catch (err) {
            setLoans([]);
        }
        setLoading(false);
    };

    const isValidDate = (date: string) => {
        // Regex for YYYY-MM-DD
        return /^\d{4}-\d{2}-\d{2}$/.test(date);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!startDate) {
            setError("Startdatum måste anges.");
            return;
        }
        if (!isValidDate(startDate)) {
            setError("Startdatum måste vara i formatet ÅÅÅÅ-MM-DD.");
            return;
        }
        if (endDate && !isValidDate(endDate)) {
            setError("Slutdatum måste vara i formatet ÅÅÅÅ-MM-DD.");
            return;
        }
        const carNosArray = carNo.split(',').map(s => s.trim()).filter(Boolean);
        if (carNosArray.length === 0) {
            setError("Minst ett bilnummer måste anges.");
            return;
        }
        try {
            await axios.post(
                "/car-loans",
                { akareNo, carNos: carNosArray, startDate, endDate },
                { withCredentials: true }
            );
            setAkareNo("");
            setCarNo("");
            setStartDate("");
            setEndDate("");
            fetchLoans();
        } catch (err) {
            // Optionally handle errors
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await axios.delete(`/car-loans/${id}`, { withCredentials: true });
            fetchLoans();
        } catch (err) {
            // Optionally handle errors
        }
    };

    return (
        <div className="container mx-auto mt-8 w-[80%]">
            <Card>
                <Title className="mb-6">Bilägare</Title>
                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8"
                >
                    <div className="col-span-1 md:col-span-2">
                        <Text>Bilnummer (kommaseparerade):<span className="text-red-500"> *</span></Text>
                        <TextInput
                            type="text"
                            value={carNo}
                            onValueChange={setCarNo}
                            placeholder="Ex: 8505, 8407"
                            required
                        />
                    </div>
                    <div className="col-span-1">
                        <Text>Åkarenummer: <span className="text-red-500"> *</span></Text>
                        <TextInput
                            type="number"
                            value={akareNo}
                            onValueChange={setAkareNo}
                            placeholder="Åkarenummer"
                            required
                        />
                    </div>

                    <div className="col-span-1">
                        <Text>Startdatum: <span className="text-red-500"> *</span></Text>
                        <TextInput
                            type="text"
                            value={startDate}
                            onValueChange={setStartDate}
                            placeholder="ÅÅÅÅ-MM-DD"
                            required
                        />
                    </div>
                    <div className="col-span-1">
                        <Text>Slutdatum: (valfritt)</Text>
                        <TextInput
                            type="text"
                            value={endDate}
                            onValueChange={setEndDate}
                            placeholder="ÅÅÅÅ-MM-DD"
                        />
                    </div>
                    <div className="col-span-1 flex items-end">
                        <Button type="submit" size="xs" className="w-full">
                            Spara
                        </Button>
                    </div>
                </form>
                {error && <Text className="text-red-500 mb-4">{error.replace('Startdate', 'Startdatum').replace('Enddate', 'Slutdatum').replace('is required', 'måste anges').replace('must be in YYYY-MM-DD format.', 'måste vara i formatet ÅÅÅÅ-MM-DD.')}</Text>}
                <Divider>Existerande relationer</Divider>
                {loading ? (
                    <Text>Laddar...</Text>
                ) : loans.length === 0 ? (
                    <Text>Inga relationer hittades.</Text>
                ) : (
                    <div>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full text-left border">
                                <thead>
                                    <tr className="bg-slate-100">
                                        <th className="px-4 py-2">Åkarenummer</th>
                                        <th className="px-4 py-2">Bilnummer</th>
                                        <th className="px-4 py-2">Startdatum</th>
                                        <th className="px-4 py-2">Slutdatum</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loans.map(loan => (
                                        <tr key={loan.id} className="border-b">
                                            <td className="px-4 py-2">{loan.akareNo}</td>
                                            <td className="px-4 py-2">{loan.carNos && loan.carNos.length > 0 ? loan.carNos.join(', ') : '-'}</td>
                                            <td className="px-4 py-2 whitespace-nowrap">{loan.startDate?.split('T')[0]}</td>
                                            <td className="px-4 py-2 whitespace-nowrap">{loan.endDate ? loan.endDate.split('T')[0] : "-"}</td>
                                            <td className="px-4 py-2">
                                                <Button
                                                    size="xs"
                                                    color="red"
                                                    onClick={() => handleDelete(loan.id)}
                                                >
                                                    Ta bort
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="md:hidden space-y-2">
                            {loans.map(loan => (
                                <div key={loan.id} className="bg-slate-50 rounded p-3 border flex flex-col gap-1">
                                    <div><span className="font-semibold">Åkarenummer:</span> {loan.akareNo}</div>
                                    <div><span className="font-semibold">Bilnummer:</span> {loan.carNos && loan.carNos.length > 0 ? loan.carNos.join(', ') : '-'}</div>
                                    <div><span className="font-semibold">Startdatum:</span> {loan.startDate?.split('T')[0]}</div>
                                    <div><span className="font-semibold">Slutdatum:</span> {loan.endDate ? loan.endDate.split('T')[0] : "-"}</div>
                                    <Button
                                        size="xs"
                                        color="red"
                                        className="mt-2 w-full"
                                        onClick={() => handleDelete(loan.id)}
                                    >
                                        Ta bort
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>
            <div>
                
            </div>
        </div>
    );
}
